import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useMemo, useState } from "react";
import { apiVariable } from "../../api/apiVariable";
import { getEmployeePtoSubPolicyAssignments } from "../../api/ptoAccrualSubPolicyService";
import { TimeOffPolicyEnum } from "../../enums/HR/TimeOffPolicyEnum";
import useAuth from "../../hooks/useAuth";
import { EmployeeDto } from "../../interfaces/HR/employee";
import PersistedDataGrid from "../../components/PersistedDataGrid/PersistedDataGrid";

interface TimeOffCalculationDto {
  id: string;
  employeeId: string;
  employeeTimeOffId: string;
  timeOffRequestId?: string | null;
  policy: string | number;
  type: string | number;
  calculationDate: string;
  days: number;
  accrualRate?: number | null;
  hours?: number | null;
  balanceAfter: number;
  note?: string | null;
  approvedBy?: string | null;
  description?: string | null;
}

type PolicyKey = keyof typeof TimeOffPolicyEnum;
type CalculationRule = "MD" | "US";

const apiPolicyNamesByValue: Record<number, string> = {
  1: "Vacation",
  2: "SickLeave",
  3: "AdditionalHolidays",
  4: "UnpaidLeave",
  5: "ParentalLeave",
  6: "WorkFromHome",
  7: "ShortDay",
  8: "PublicHoliday",
  9: "PaidTimeOff",
  10: "FloatingHolidays",
};

const policyKeysByApiName: Record<string, PolicyKey> = {
  Vacation: "vacation",
  SickLeave: "sickLeave",
  AdditionalHolidays: "additionalHolidays",
  UnpaidLeave: "unpaidLeave",
  ParentalLeave: "parentalLeave",
  WorkFromHome: "workFromHome",
  ShortDay: "shortDay",
  PublicHoliday: "publicHoliday",
  PaidTimeOff: "paidTimeOff",
  FloatingHolidays: "floatingHolidays",
};

const calculationTypeLabelsByValue: Record<number, string> = {
  1: "Opening Balance",
  2: "Accrual",
  3: "Usage",
  4: "Reversal",
  5: "Manual Adjustment",
};

const policyOptions = Object.entries(TimeOffPolicyEnum).map(([key, label]) => ({
  key: key as PolicyKey,
  label,
  apiName:
    Object.entries(policyKeysByApiName).find(
      ([, value]) => value === key,
    )?.[0] ?? key,
}));

const formatEnumLabel = (value: string | number) => {
  const rawValue = value?.toString() ?? "";
  if (!rawValue) return "";

  return rawValue
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/^\w/, (match) => match.toUpperCase());
};

const resolvePolicyKey = (policy: string | number): PolicyKey | undefined => {
  if (typeof policy === "number") {
    return policyKeysByApiName[apiPolicyNamesByValue[policy]];
  }

  const normalized = policy?.toString();
  if (!normalized) return undefined;

  if (normalized in TimeOffPolicyEnum) return normalized as PolicyKey;

  return (
    policyKeysByApiName[normalized] ??
    policyKeysByApiName[formatEnumLabel(normalized).replace(/ /g, "")]
  );
};

const getPolicyLabel = (policy: string | number) => {
  const key = resolvePolicyKey(policy);
  if (key) return TimeOffPolicyEnum[key];

  if (typeof policy === "number") {
    return apiPolicyNamesByValue[policy] ?? policy.toString();
  }

  return formatEnumLabel(policy);
};

const getCalculationTypeLabel = (type: string | number) => {
  if (typeof type === "number") {
    return calculationTypeLabelsByValue[type] ?? type.toString();
  }

  const parsedType = Number(type);
  if (!Number.isNaN(parsedType) && calculationTypeLabelsByValue[parsedType]) {
    return calculationTypeLabelsByValue[parsedType];
  }

  return formatEnumLabel(type);
};

const formatDate = (date: string) => {
  if (!date) return "";

  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) return date;

  return parsedDate.toLocaleDateString();
};

const WORKING_HOURS_PER_DAY = Number(
  process.env.REACT_APP_WORKING_HOURS_PER_DAY ?? 8,
);

const formatNumber = (value: number) =>
  Number(value ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

const formatOptionalNumber = (value?: number | null) =>
  value == null ? "—" : formatNumber(Number(value));

const formatOptionalText = (value?: string | null) => value?.trim() || "—";

const TimeOffCalculationsPage = () => {
  const { token } = useAuth();
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [selectedPolicy, setSelectedPolicy] = useState<PolicyKey | "">("");
  const [calculationRule, setCalculationRule] = useState<CalculationRule>("MD");

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token],
  );

  const {
    data: employees = [],
    isLoading: employeesLoading,
    isError: isEmployeesError,
  } = useQuery({
    queryKey: ["employees", "timeOffCalculations", "active"],
    enabled: Boolean(token),
    queryFn: async () => {
      const { data } = await axios.get<EmployeeDto[]>(
        `${apiVariable}/getAllEmployees`,
        {
          headers,
          params: { areActive: true },
        },
      );
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const selectedEmployee = employees.find(
    (employee) => employee.id === selectedEmployeeId,
  );

  const selectedPolicyOption = policyOptions.find(
    (option) => option.key === selectedPolicy,
  );

  const { data: ptoAssignments = [], isLoading: assignmentsLoading } = useQuery(
    {
      queryKey: ["ptoSubPolicyAssignments", selectedEmployeeId],
      enabled: Boolean(token) && Boolean(selectedEmployeeId),
      queryFn: () =>
        getEmployeePtoSubPolicyAssignments(token!, selectedEmployeeId),
    },
  );

  const currentPtoAssignment = useMemo(() => {
    const today = new Date().getTime();

    return [...ptoAssignments]
      .filter(
        (assignment) => new Date(assignment.effectiveDate).getTime() <= today,
      )
      .sort(
        (a, b) =>
          new Date(b.effectiveDate).getTime() -
          new Date(a.effectiveDate).getTime(),
      )[0];
  }, [ptoAssignments]);

  const {
    data: calculations = [],
    isLoading: calculationsLoading,
    isError: isCalculationsError,
    error: calculationsError,
  } = useQuery({
    queryKey: [
      "timeOffCalculations",
      selectedEmployeeId,
      selectedPolicyOption?.apiName ?? "all",
      calculationRule,
      currentPtoAssignment?.ptoAccrualSubPolicyId ?? "no-sub-policy",
    ],
    enabled:
      Boolean(token) && Boolean(selectedEmployeeId) && !assignmentsLoading,
    queryFn: async () => {
      const { data } = await axios.get<TimeOffCalculationDto[]>(
        `${apiVariable}/getEmployeeTimeOffCalculations/${selectedEmployeeId}`,
        {
          headers,
          params: {
            ...(selectedPolicyOption
              ? { policy: selectedPolicyOption.apiName }
              : {}),
            calculationRule,
            ...(currentPtoAssignment
              ? {
                  ptoAccrualSubPolicyId:
                    currentPtoAssignment.ptoAccrualSubPolicyId,
                }
              : {}),
          },
        },
      );
      return data;
    },
  });

  const rows = useMemo(
    () =>
      calculations.map((calculation) => ({
        ...calculation,
        policyLabel: getPolicyLabel(calculation.policy),
        typeLabel: getCalculationTypeLabel(calculation.type),
        calculationDateFormatted: formatDate(calculation.calculationDate),
        displayChange:
          resolvePolicyKey(calculation.policy) === "paidTimeOff" &&
          !currentPtoAssignment
            ? null
            : calculationRule === "MD"
              ? Number(calculation.days ?? 0)
              : Number(
                  calculation.hours ??
                    Number(calculation.days ?? 0) * WORKING_HOURS_PER_DAY,
                ),
        displayStartBalance:
          resolvePolicyKey(calculation.policy) === "paidTimeOff" &&
          !currentPtoAssignment
            ? null
            : (Number(calculation.balanceAfter ?? 0) -
                Number(calculation.days ?? 0)) *
              (calculationRule === "US" ? WORKING_HOURS_PER_DAY : 1),
        displayBalanceAfter:
          resolvePolicyKey(calculation.policy) === "paidTimeOff" &&
          !currentPtoAssignment
            ? null
            : Number(calculation.balanceAfter ?? 0) *
              (calculationRule === "US" ? WORKING_HOURS_PER_DAY : 1),
        note: calculation.note ?? null,
        approvedBy: calculation.approvedBy ?? null,
      })),
    [calculationRule, calculations, currentPtoAssignment],
  );

  const unitLabel = calculationRule === "MD" ? "days" : "hours";

  const columns: GridColDef[] = [
    {
      field: "calculationDate",
      headerName: "Calculation Date",
      flex: 0.9,
      minWidth: 150,
      valueFormatter: (value) => formatDate(value as string),
    },
    {
      field: "policyLabel",
      headerName: "Policy",
      flex: 0.9,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value}
          size="small"
          color="primary"
          variant="outlined"
        />
      ),
    },
    {
      field: "typeLabel",
      headerName: "Type",
      flex: 0.8,
      minWidth: 150,
    },
    {
      field: "displayStartBalance",
      headerName: `Start Balance (${unitLabel})`,
      type: "number",
      flex: 0.7,
      minWidth: 140,
      valueFormatter: (value) => formatOptionalNumber(value as number | null),
    },
    {
      field: "displayChange",
      headerName: `Change (${unitLabel})`,
      type: "number",
      flex: 0.5,
      minWidth: 100,
      valueFormatter: (value) => formatOptionalNumber(value as number | null),
    },
    {
      field: "displayBalanceAfter",
      headerName: `Balance After (${unitLabel})`,
      type: "number",
      flex: 0.7,
      minWidth: 140,
      valueFormatter: (value) => formatOptionalNumber(value as number | null),
    },
    {
      field: "note",
      headerName: "Note",
      flex: 1.2,
      minWidth: 200,
      valueFormatter: (value) => formatOptionalText(value as string | null),
    },
    {
      field: "approvedBy",
      headerName: "Approved By",
      flex: 0.9,
      minWidth: 160,
      valueFormatter: (value) => formatOptionalText(value as string | null),
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1.4,
      minWidth: 220,
    },
  ];

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Time Off Calculations
      </Typography>

      <ToggleButtonGroup
        value={calculationRule}
        exclusive
        onChange={(_, value: CalculationRule | null) =>
          value && setCalculationRule(value)
        }
        aria-label="Time off calculation rule"
        size="small"
        sx={{ mb: 2 }}
      >
        <ToggleButton value="MD" aria-label="MD calculation rules">
          MD
        </ToggleButton>
        <ToggleButton value="US" aria-label="US calculation rules">
          US
        </ToggleButton>
      </ToggleButtonGroup>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={7}>
            <Autocomplete
              options={employees}
              loading={employeesLoading}
              loadingText="Loading employees..."
              value={selectedEmployee ?? null}
              onChange={(_, employee) =>
                setSelectedEmployeeId(employee?.id ?? "")
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(employee) =>
                `${employee.firstName} ${employee.lastName}`.trim()
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Employee"
                  placeholder="Search employee"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {employeesLoading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  fullWidth
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <FormControl fullWidth>
              <InputLabel id="time-off-policy-filter-label">Policy</InputLabel>
              <Select
                labelId="time-off-policy-filter-label"
                label="Policy"
                value={selectedPolicy}
                onChange={(event) =>
                  setSelectedPolicy(event.target.value as PolicyKey | "")
                }
              >
                <MenuItem value="">All Policies</MenuItem>
                {policyOptions.map((option) => (
                  <MenuItem key={option.key} value={option.key}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {selectedEmployee && (
          <Stack spacing={0.5} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Position: {selectedEmployee.position} • Department:{" "}
              {selectedEmployee.department}
            </Typography>
            {!assignmentsLoading && (
              <Typography variant="body2" color="text.secondary">
                PTO sub-policy:{" "}
                {currentPtoAssignment?.subPolicyName ?? "None assigned"}
              </Typography>
            )}
          </Stack>
        )}
      </Paper>

      {isEmployeesError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load employees.
        </Alert>
      )}

      {isCalculationsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load time off calculations
          {calculationsError instanceof Error
            ? `: ${calculationsError.message}`
            : "."}
        </Alert>
      )}

      {!selectedEmployeeId ? (
        <Alert severity="info">
          Select an employee to view time off calculations.
        </Alert>
      ) : calculationsLoading || assignmentsLoading ? (
        <Box
          sx={{
            flexWrap: { xs: "wrap", lg: "nowrap" },
            display: "flex",
            justifyContent: "center",
            mt: 4,
          }}
        >
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Alert severity="info">
          No time off calculations found for this employee.
        </Alert>
      ) : (
        <Stack spacing={2}>
          <Box>
            <Grid container spacing={2}></Grid>
          </Box>

          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Calculation Details by Policy
            </Typography>
            <PersistedDataGrid
              tableName="timeOffCalculations"
              rows={rows}
              columns={columns}
              getRowId={(row) => row.id}
              autoHeight
              initialState={{
                sorting: {
                  sortModel: [{ field: "calculationDate", sort: "desc" }],
                },
              }}
            />
          </Paper>
        </Stack>
      )}
    </Box>
  );
};

export default TimeOffCalculationsPage;
